import React from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { step2Styles } from "./unitSelectionStep.styles";

const UnitSelectionView = ({
  cantidadUnidades,
  closeSnack,
  generatingSituations,
  handleContinuar,
  loading,
  onBack,
  openRegenerateDialog,
  regenerateSingleUnit,
  regenerateUnitsList,
  regeneratingUnitId,
  regeneratingUnits,
  seleccionados,
  setCantidadUnidades,
  setOpenRegenerateDialog,
  snack,
  toKey,
  toggleCheckbox,
  unidadesBackend,
}) => {
  if (loading) {
    return (
      <Box className="step2-root" sx={step2Styles}>
        <Backdrop
          open
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.modal + 1,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0, 0, 0, 0.55)",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress color="inherit" />
            <Typography sx={{ mt: 2, fontWeight: 500 }}>
              Cargando unidades SAC...
            </Typography>
          </Box>
        </Backdrop>
      </Box>
    );
  }

  return (
    <Box className="step2-root" sx={step2Styles}>
      <Typography variant="h4" gutterBottom className="step2-title">
        Selección de Unidades Didácticas
      </Typography>

      <Box className="step2-filter" sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, marginBottom: 3 }}>
        <Typography variant="body1" className="step2-filter-label">
          Seleccione la cantidad de unidades:
        </Typography>

        <Select
          value={cantidadUnidades}
          onChange={(event) => {
            const nuevoValor = Number(event.target.value);
            if (nuevoValor < cantidadUnidades && seleccionados.length > nuevoValor) {
              setCantidadUnidades(nuevoValor);
              return;
            }
            setCantidadUnidades(nuevoValor);
          }}
          className="step2-select"
          sx={{ width: 120 }}
        >
          {[8, 9, 10, 11, 12].map((num) => (
            <MenuItem key={num} value={num}>{num}</MenuItem>
          ))}
        </Select>

        <Box sx={{ marginLeft: { xs: 0, sm: "auto" }, width: { xs: "100%", sm: "auto" }, display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
          <Button
            variant="outlined"
            onClick={() => setOpenRegenerateDialog(true)}
            disabled={loading || generatingSituations || regeneratingUnits || regeneratingUnitId !== null}
            className="step2-btn step2-btn-back"
          >
            {regeneratingUnits ? "Regenerando..." : "Regenerar lista"}
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} className="step2-table">
        <Table className="step2-table-inner">
          <TableHead className="step2-thead">
            <TableRow>
              <TableCell className="step2-th step2-th-check">Seleccionar</TableCell>
              <TableCell className="step2-th">Unidad</TableCell>
              <TableCell className="step2-th">Prioridad</TableCell>
              <TableCell className="step2-th step2-th-actions">Acciones</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {unidadesBackend.map((unidad) => {
              const key = toKey(unidad.id);
              const checked = (seleccionados || []).map(toKey).includes(key);
              const disabled = !checked && (seleccionados || []).length >= Number(cantidadUnidades);

              return (
                <TableRow key={key} className="step2-tr">
                  <TableCell className="step2-td step2-td-check">
                    <Checkbox
                      color="default"
                      checked={checked}
                      onChange={() => toggleCheckbox(unidad.id)}
                      disabled={disabled}
                      className="step2-checkbox"
                      inputProps={{ "aria-label": `Seleccionar unidad ${unidad.nombre}` }}
                    />
                  </TableCell>

                  <TableCell className="step2-td step2-td-text">{unidad.nombre}</TableCell>
                  <TableCell className="step2-td step2-td-text">{unidad.prioridad}</TableCell>

                  <TableCell className="step2-td step2-td-actions">
                    <Button
                      variant="outlined"
                      onClick={() => regenerateSingleUnit(unidad)}
                      disabled={loading || generatingSituations || regeneratingUnits || regeneratingUnitId !== null}
                      className="step2-btn step2-btn-back step2-btn-inline"
                    >
                      {regeneratingUnitId === unidad.id ? "Regenerando..." : "Regenerar"}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className="step2-actions" sx={{ marginTop: 3, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Button variant="outlined" onClick={onBack} className="step2-btn step2-btn-back">
          Atrás
        </Button>

        <Button
          variant="contained"
          onClick={handleContinuar}
          disabled={seleccionados.length !== Number(cantidadUnidades) || generatingSituations}
          className="step2-btn step2-btn-next"
        >
          {generatingSituations ? "Generando..." : "Continuar"}
        </Button>
      </Box>

      <Backdrop
        open={generatingSituations}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 1,
          backdropFilter: "blur(4px)",
          backgroundColor: "rgba(0, 0, 0, 0.55)",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 2, fontWeight: 500 }}>Generando situaciones significativas...</Typography>
        </Box>
      </Backdrop>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={closeSnack} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={closeSnack} severity={snack.severity} variant="filled" sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>

      <Dialog open={openRegenerateDialog} onClose={() => !regeneratingUnits && setOpenRegenerateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Regenerar lista de unidades</DialogTitle>
        <DialogContent>
          <Typography sx={{ lineHeight: 1.6 }}>
            Se generará una nueva lista de unidades didácticas y se limpiará la selección actual. Si ya habías generado situaciones significativas para esta lista, también se descartarán.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRegenerateDialog(false)} disabled={regeneratingUnits}>Cancelar</Button>
          <Button variant="contained" onClick={regenerateUnitsList} disabled={regeneratingUnits}>
            {regeneratingUnits ? "Regenerando..." : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnitSelectionView;
